import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BarcodeIcon, Boxes, Layers, MapPin, ScanBarcode, Warehouse, X } from "lucide-react";

import { getTenantId } from "../../../redux/features/auth/authSlice";
import {
  useExportLiveInventoryExcelMutation,
  useGetInventorySummaryQuery,
} from "../../../redux/services/inventoryApiSlice";
import { removeEmptyKeys } from "../../../utils";
import { useTenantSitesAndZones } from "../../../hooks/useTenantSitesAndZones";
import { Page } from "../../../routes/config";

import {
  extendTrail,
  makeTrailItem,
  writeReportTrailState,
} from "./reportTrail";
import { exportRowsToWorkbook } from "./exportWorkbook";

import { UIAgGridTable, type UIAgGridColumn } from "../../common/agGridTable";
import { Pagination } from "../../common/pagination";
import { ReportToolbar, ReportFilterChips } from "../../common/reportToolbar";
import Select from "../../common/selectInput/Select";
import Input from "../../common/input/Input";

import "./inventory-summary.css";
import { MetricCard } from "./components/MetricCard";
import { MetricSkeleton } from "./components/MetricSkeleton";
import { useExportHandler } from "../../../hooks/useExportHandler";
import { MarkerChips } from "./components/MarkerChips";

type InventorySummaryRow = {
  key: string;
  upc: string;
  skuUpc: string;
  description: string;
  siteId: string;
  zoneId: string;
  siteName: string;
  zoneName: string;
  qty: number;
};

const resolveDescription = (row: any) =>
  String(
    row?.description ??
      row?.productDescription ??
      row?.product?.description ??
      row?.product?.name ??
      row?.itemDescription ??
      row?.materialDescription ??
      "",
  ).trim();

const InventorySummary = () => {
  const tenantId = useSelector(getTenantId);
  const navigate = useNavigate();
  const location = useLocation();

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [sku, setSku] = useState("");
  const [siteId, setSiteId] = useState<string>();
  const [zoneId, setZoneId] = useState<string>();

  const [filters, setFilters] = useState({
    sku: "",
    search: "",
    siteId: undefined as string | undefined,
    zoneId: undefined as string | undefined,
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [sortConfig, setSortConfig] = useState<{
    field?: string;
    order?: "ascend" | "descend";
  }>({});

  const { zoneRows, siteNameById, siteOptionsById, zoneOptionsById } =
    useTenantSitesAndZones(tenantId);

  const summaryParams = useMemo(
    () =>
      removeEmptyKeys({
        tenantId,
        upc: filters.sku || filters.search || undefined,
        siteId: filters.siteId,
        zoneId: filters.zoneId,
        groupBy: "sku,site,zone",
        page,
        limit: pageSize,
      }),
    [tenantId, filters, page, pageSize],
  );

  const { data, isFetching } = useGetInventorySummaryQuery(summaryParams, {
    skip: !tenantId,
  });

  const rows = useMemo(
    () => data?.result?.rows ?? data?.result?.data ?? [],
    [data],
  );

  const totalCount =
    data?.result?.count ??
    data?.result?.totalCount ??
    data?.result?.total ??
    rows.length;

  const tableData: InventorySummaryRow[] = useMemo(() => {
    const grouped = new Map<string, InventorySummaryRow>();

    rows.forEach((r: any) => {
      const upc = String(r.upc ?? r.sku ?? "").trim();
      const skuUpc = String(r.sku ?? r.upc ?? "");
      const description = resolveDescription(r);
      const siteId = String(r.siteId ?? r.site?.id ?? "");
      const zoneId = String(r.zoneId ?? r.zone?.id ?? "");
      const siteName =
        String(r.siteName ?? r.site?.name ?? "") ||
        siteNameById.get(siteId) ||
        "";
      const zoneName = String(r.zoneName ?? r.zone?.name ?? "") || zoneId;

      const key = `${upc}|${siteId}|${zoneId}`;
      const qty = Number(r.qty ?? r.totalCount ?? r.count ?? 0);
      const markerMatrix =
        r.additionalData?.association?.map((a: any) =>
          String(a.type).toUpperCase(),
        ) || [];
      if (grouped.has(key)) {
        grouped.get(key)!.qty += qty;
      } else {
        grouped.set(key, {
          key,
          upc,
          skuUpc,
          description,
          siteId,
          zoneId,
          siteName,
          zoneName,
          qty,
          markerMatrix,
        });
      }
    });

    return Array.from(grouped.values());
  }, [rows, siteNameById]);

  const sortedTableData = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return tableData;

    const dir = sortConfig.order === "ascend" ? 1 : -1;

    return [...tableData].sort((a, b) => {
      const field = sortConfig.field as keyof InventorySummaryRow;

      if (field === "qty") {
        return (Number(a[field]) - Number(b[field])) * dir;
      }

      return String(a[field] ?? "").localeCompare(String(b[field] ?? "")) * dir;
    });
  }, [tableData, sortConfig]);

  const applyFilters = useCallback(() => {
    setFilters({
      sku: sku.trim(),
      search: !sku.trim() ? searchValue.trim() : "",
      siteId,
      zoneId,
    });

    setShowAdvancedFilters(false);
    setPage(1);
  }, [sku, searchValue, siteId, zoneId]);

  const resetFilters = useCallback(() => {
    setSku("");
    setSearchValue("");
    setSiteId(undefined);
    setZoneId(undefined);

    setFilters({
      sku: "",
      search: "",
      siteId: undefined,
      zoneId: undefined,
    });

    setPage(1);
  }, []);

  const { handleExport, isExportLoading, ExportLimitModal } = useExportHandler({
    exportMutation: useExportLiveInventoryExcelMutation,
    listFetchParams: summaryParams,
    totalCount,
    onRefineFilters: () => setShowAdvancedFilters(true),
  });

  const exportExcel = useCallback(() => {
    const rows = [
      ["UPC", "Description", "Site", "Zone", "QTY"],
      ...sortedTableData.map((r) => [
        r.skuUpc,
        r.description,
        r.siteName,
        r.zoneName,
        r.qty,
      ]),
    ];

    exportRowsToWorkbook(
      `inventory-summary-${new Date().toISOString().slice(0, 10)}.xlsx`,
      "Inventory Summary",
      rows,
    );
  }, [sortedTableData]);

  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (filters.siteId) {
      chips.push({
        label: `Site: ${siteNameById.get(filters.siteId) ?? filters.siteId}`,
        onRemove: () => {
          setSiteId(undefined);
          setZoneId(undefined);
          setFilters((prev) => ({
            ...prev,
            siteId: undefined,
            zoneId: undefined,
          }));
          setPage(1);
        },
      });
    }

    if (filters.zoneId) {
      const zone =
        zoneRows.find((z) => String(z.id) === String(filters.zoneId))?.name ??
        filters.zoneId;

      chips.push({
        label: `Zone: ${zone}`,
        onRemove: () => {
          setZoneId(undefined);
          setFilters((prev) => ({
            ...prev,
            zoneId: undefined,
          }));
          setPage(1);
        },
      });
    }

    if (filters.sku) {
      chips.push({
        label: `SKU: ${filters.sku}`,
        onRemove: () => {
          setSku("");
          setSearchValue("");
          setFilters((prev) => ({
            ...prev,
            sku: "",
            search: "",
          }));
          setPage(1);
        },
      });
    }

    return chips;
  }, [filters, siteNameById, zoneRows]);

  const columns: UIAgGridColumn<InventorySummaryRow>[] = [
    {
      key: "markerMatrix",
      title: "ACTIVE MARKER MATRIX",
      dataIndex: "markerMatrix",
      width: 160,
      minWidth: 140,
      sorter: false,
      render: (_: any, row: any) => (
        <div className="enb-marker-cell">
          <MarkerChips matrix={row.markerMatrix ?? []} />
        </div>
      ),
    },
    {
      key: "skuUpc",
      title: "UPC",
      dataIndex: "skuUpc",
      flex: 1,
      sorter: true,
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      flex: 1,
      sorter: true,
    },
    {
      key: "siteName",
      title: "Site",
      dataIndex: "siteName",
      flex: 1,
      sorter: true,
    },
    {
      key: "zoneName",
      title: "Zone",
      dataIndex: "zoneName",
      flex: 1,
      sorter: true,
    },
    {
      key: "qty",
      title: "QTY",
      dataIndex: "qty",
      width: 100,
      headerClass: "center-header",
      sorter: true,
      render: (value: unknown) => (
        <div className="text-center w-full">{String(value ?? "0")}</div>
      ),
    },
  ];

  const isInitialLoad = true;
  return (
    <div className="h-full flex flex-col min-h-0 rounded-md gap-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 shrink-0">
        {isInitialLoad && isFetching ? (
          <>
            <MetricSkeleton />
            <MetricSkeleton />
            <MetricSkeleton />
            {/* <MetricSkeleton /> */}
          </>
        ) : (
          <>
            <MetricCard
              label="TOTAL ITEMS:"
              value={totalCount}
              accent="default"
              icon={<Boxes />}
            />
            <MetricCard
              label="UNIQUE SKUs (UPCs)"
              value={data?.result?.uniqueSkuCount}
              accent="orange"
              icon={<ScanBarcode />}
            />
            <MetricCard
              label="MONITORED FACILITIES"
              value={data?.result?.uniqueZoneCount}
              accent="green"
              icon={<Warehouse />}
              helperText="Zones"
            />
          </>
        )}
      </div>
      <div className="mt-4 bg-white">
        <ReportToolbar
          searchValue={searchValue}
          onSearchChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              search: value,
            }))
          }
          onDebounceSearch={(value) => {
            setFilters((prev) => ({
              ...prev,
              search: value.trim(),
              sku: "",
            }));
            setPage(1);
          }}
          onSearchEnter={applyFilters}
          searchPlaceholder="Search UPC"
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() => setShowAdvancedFilters((s) => !s)}
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          activeFilterCount={activeFilterChips.length}
          onExportExcel={handleExport}
          onExportLoading={isExportLoading}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Site Location
              </label>
              <Select
                placeholder="Select Site"
                value={siteId}
                onChange={(e) => {
                  setSiteId(e.target.value);
                  setZoneId(undefined);
                }}
                allowClear
                onClear={() => {
                  setSiteId(undefined);
                  setZoneId(undefined);
                }}
                options={siteOptionsById}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                Zone
              </label>
              <Select
                placeholder="Select Zone"
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                allowClear
                onClear={() => setZoneId(undefined)}
                options={zoneOptionsById}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                UPC
              </label>
              <Input
                placeholder="Please enter UPC"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                allowClear
              />
            </div>
          </div>
        </ReportToolbar>
        <ReportFilterChips chips={activeFilterChips} onClearAll={resetFilters} />
      </div>
      {ExportLimitModal}

      <div className="table-container">
        <UIAgGridTable
          columns={columns}
          data={sortedTableData}
          loading={isFetching}
          rowHeight={44}
          onSortChange={setSortConfig}
          onRowClick={(record) => {
            const params = new URLSearchParams();

            if (record.skuUpc) params.set("sku", record.skuUpc);
            if (record.siteId) params.set("siteId", record.siteId);
            if (record.zoneId) params.set("zoneId", record.zoneId);

            const path = `${Page.REPORTS_INVENTORY_DETAILS}?${params.toString()}`;
            const nextTrail = extendTrail(
              location,
              makeTrailItem(`Inventory Details (${record.skuUpc})`, path),
            );

            navigate(path, {
              state: writeReportTrailState(location.state, nextTrail),
            });
          }}
        />

        <Pagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPage(1);
            setPageSize(size);
          }}
        />
      </div>
    </div>
  );
};

export default InventorySummary;
